import { CriticalDataRealtime } from './realtime/useCriticalDataRealtime';
// import './App.css';
import React, { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { Provider, useDispatch, useSelector } from "react-redux";
import { store } from "./redux/store";
import JobListContainer from "./components/MainPage/JobListContainer";
import JobGeneralInfo from "./components/JobDetailPage/General/JobGeneralInfo";
import JobSalesInfo from "./components/JobDetailPage/Sales/JobSalesInfo";
import JobMechSpecs from "./components/JobDetailPage/Mechanical/JobMechInfo";
import JobElecSpecs from "./components/JobDetailPage/Electrical/JobElecInfo";
import GlobalSearch from "./components/MainPage/GlobalSearch";
import ChangeLog from "./components/MainPage/ChangeLog";
import JobQa from "./components/JobDetailPage/JobQa";
import Home from "./components/MainPage/Home";
import FormsPage from "./components/FormsPage/FormsPage";
import AppsContainer from "./components/AppsPage/AppsContainer";
import PanelShopContainer from "./components/PanelShopPage/PanelShopContainer";
import PanelShopWorkProcedures from "./components/PanelShopPage/PanelShopWorkProcedures";
import {
  fetchUserRoles,
  clearUserRoles,
} from "./redux/slices/userSlice";
import ProtectedRoute from "./components/TopNav/ProtectedRoutes";
import { AuthProvider } from "./contexts/AuthContext";
import { SignalRProvider } from "./contexts/SignalRContext";
import { PortalRealtimeProvider } from "./realtime/PortalRealtimeProvider";
import { resetApiUrls } from "./redux/slices/apiUrlSlice";
import AdditionalItems from "./components/JobDetailPage/Additional/AdditionalItems";
import Forms from "./components/JobDetailPage/Forms/Forms";
import ChangeOrderContainer from "./components/JobDetailPage/ChangeOrders/ChangeOrderContainer";
import UserPageContainer from "./components/UserPage/UserPageContainer";
import NPContainer from "./components/UserPage/NPContainer";
import JobScheduleContainer from './components/JobSchedulePage/JobScheduleContainer';
import EnvironmentDetector from './functions/EnvironmentDetector';
import AdminContainer from './components/AdminPage/AdminContainer';
import { HelpProvider } from './help/HelpProvider';
import ControlsContainer from './components/ControlsPage/ControlsContainer';
import AssemblyContainer from './components/AssemblyPage/AssemblyContainer';
import ShippingContainer from './components/ShippingPage/ShippingContainer';
import EngineeringContainer from './components/EngineeringPage/EngineeringContainer';
import EngineeringDesignDocuments from './components/EngineeringPage/EngineeringDesignDocuments';
import SalesPortalContainer from './components/SalesPortal/SalesPortalContainer';
import ProductionReportsContainer from './components/ReportsPage/ProductionReportsContainer';
import {
  fetchUserDropdowns,
  selectLastUserDropdownFetch,
} from "./redux/slices/dropdownSlice";
import { useGetCriticalDataQuery } from "./redux/api/portalApi";
import { criticalDataScope } from './redux/api/criticalDataContract';
import { ROUTE_PREFIX } from "./config/routePrefix";
import { ENGINEERING_CONTAINER_PATHS } from "./config/engineeringRoutes";
import {
  PRODUCTION_ASSEMBLY_CONTAINER_PATHS,
  PRODUCTION_SHIPPING_CONTAINER_PATHS,
  PRODUCTION_ASSEMBLY_SCHEDULE_PATH,
  PRODUCTION_NORTH_PRAIRIE_CONTAINER_PATHS,
  PRODUCTION_NORTH_PRAIRIE_PATH,
  PRODUCTION_PORTAL_PATH,
  PRODUCTION_PANEL_SHOP_PATH,
  PRODUCTION_PANEL_SHOP_TAB_ROUTES,
  PRODUCTION_REPORT_CONTAINER_PATHS,
  PRODUCTION_SCHEDULE_CONTAINER_PATHS,
  PRODUCTION_SCHEDULE_PATH,
} from "./config/productionRoutes";

const App = () => {
  const { instance, inProgress, accounts } = useMsal();
  const [isMsalInitialized, setIsMsalInitialized] = useState(false);
  const roleAccount = useRef(null);
  const dispatch = useDispatch();
  const lastUserDropdownFetch = useSelector(selectLastUserDropdownFetch);

  // ── RTK Query: auto-fetch critical data once MSAL is ready ──────────────
  const isMsalReady = accounts.length > 0 && inProgress === "none";
  const criticalScope = useSelector(criticalDataScope);
  const criticalArgs = JSON.parse(criticalScope);
  useGetCriticalDataQuery(criticalArgs, {
    skip: !isMsalReady || !criticalArgs.accountId || criticalArgs.accountId !== accounts[0]?.localAccountId,
    refetchOnMountOrArgChange: false,
  });

  // ("User Roles: ", mapUserRoles);
  // ("Current User: ", currentUser);

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        // Wait for MSAL to handle any redirects
        await instance.handleRedirectPromise();
      } catch (error) {
        console.error("MSAL initialization error:", error);
      } finally {
        setIsMsalInitialized(true); // Mark MSAL as initialized even on redirect handling failure
      }
    };

    // Only initialize MSAL if there is no ongoing request
    if (inProgress === "none") {
      initializeMsal();
    }
  }, [instance, inProgress]);

  useEffect(() => {
    // Reset API URLs when the app loads
    dispatch(resetApiUrls());
  }, [dispatch]);

  useEffect(() => {
    // Revalidate once per account/session, including when persisted roles exist.
    // A stable account key avoids refetching on renders or token refreshes.
    if (inProgress !== 'none') return;
    const accountKey = accounts[0]?.localAccountId;
    if (!accountKey) { roleAccount.current = null; return; }
    if (roleAccount.current !== accountKey) {
      roleAccount.current = accountKey;
      dispatch(fetchUserRoles({ instance, accounts }));
    }
  }, [dispatch, instance, accounts, inProgress]);

  useEffect(() => {
    // Fetch user dropdowns (MS Graph API – separate from criticalData)
    if (
      accounts.length > 0 &&
      instance &&
      inProgress === "none" &&
      !lastUserDropdownFetch
    ) {
      console.log('App.js: Fetching user dropdowns with MSAL instance and accounts');
      dispatch(fetchUserDropdowns({ instance, accounts }));
    }
  }, [dispatch, instance, accounts, inProgress, lastUserDropdownFetch]);

  useEffect(() => {
    // Keep page-unload handling lightweight: avoid async dispatches during teardown
    const handleBeforeUnload = () => {
      dispatch(clearUserRoles());
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [dispatch]);

  if (!isMsalInitialized) {
    return <div>Loading...</div>; // You can show a loading spinner or message
  }

  return (
    <Provider store={store}>
      <AuthProvider>
        <SignalRProvider enabled={accounts.length > 0 && inProgress !== 'logout'} sessionKey={accounts[0]?.homeAccountId}>
        <PortalRealtimeProvider instance={instance} account={inProgress !== 'logout' ? accounts[0] : null}>
          <CriticalDataRealtime />
        <div className="App">
          {/* Include the EnvironmentDetector at the top level */}
          <EnvironmentDetector />
          {/* <UserRolesProvider> */}
          <Router basename={ROUTE_PREFIX}>
            <HelpProvider>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/forms" element={<ProtectedRoute permissionKey="home.forms"><FormsPage /></ProtectedRoute>} />
              {/* Protected Routes */}
              <Route
                path={PRODUCTION_PANEL_SHOP_PATH}
                element={<ProtectedRoute permissionKey="home.panel-shop"><PanelShopContainer /></ProtectedRoute>}
              >
                <Route index element={<p>Select a tab from the navigation above.</p>} />
                {PRODUCTION_PANEL_SHOP_TAB_ROUTES.map(({ id }) => (
                  <Route key={id} path={id} element={id === 'work-procedures' ? <PanelShopWorkProcedures /> : null} />
                ))}
              </Route>
              <Route
                path="/apps"
                element={
                  <ProtectedRoute permissionKey="home.apps-portal">
                    <AppsContainer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/apps/activitylog"
                element={
                  <ProtectedRoute permissionKey="home.apps-portal">
                    <AppsContainer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/apps/bookingslog"
                element={
                  <ProtectedRoute permissionKey="apps.bookings-log">
                    <AppsContainer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/apps/taskmanager"
                element={
                  <ProtectedRoute permissionKey="home.apps-portal">
                    <AppsContainer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/apps/salesTargets"
                element={
                  <ProtectedRoute permissionKey="apps.sales-targets">
                    <AppsContainer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sales/*"
                element={
                  <ProtectedRoute>
                    <SalesPortalContainer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute>
                    <AdminContainer />
                  </ProtectedRoute>
                }
              />
              <Route
                path={PRODUCTION_PORTAL_PATH}
                element={
                  <ProtectedRoute permissionKey="home.production-portal">
                    <JobListContainer />
                  </ProtectedRoute>
                }
              />
              <Route path="/jobs" element={<Navigate to={PRODUCTION_PORTAL_PATH} replace />} />
              <Route
                path="/controls/*"
                element={
                  <ProtectedRoute>
                    <ControlsContainer />
                  </ProtectedRoute>
                }
              />
              {PRODUCTION_ASSEMBLY_CONTAINER_PATHS.map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ProtectedRoute>
                      <AssemblyContainer />
                    </ProtectedRoute>
                  }
                />
              ))}
              {PRODUCTION_SHIPPING_CONTAINER_PATHS.map((path) => (
                <Route key={path} path={path} element={<ProtectedRoute><ShippingContainer /></ProtectedRoute>} />
              ))}
              <Route
                path="/assembly/*"
                element={<Navigate to={PRODUCTION_ASSEMBLY_SCHEDULE_PATH} replace />}
              />
              <Route
                path="/:id/generalInfo"
                element={
                  <ProtectedRoute>
                    <JobGeneralInfo />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:id/salesInfo"
                element={
                  <ProtectedRoute>
                    <JobSalesInfo />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:id/mechInfo"
                element={
                  <ProtectedRoute>
                    <JobMechSpecs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:id/elecInfo"
                element={
                  <ProtectedRoute>
                    <JobElecSpecs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/globalSearch"
                element={
                  <ProtectedRoute permissionKey="home.global-search">
                    <GlobalSearch />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:id/changeLog"
                element={
                  <ProtectedRoute>
                    <ChangeLog />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:id/jobQa"
                element={
                  <ProtectedRoute>
                    <JobQa />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:id/forms/:formTab?"
                element={<ProtectedRoute><Forms /></ProtectedRoute>}
              />
              <Route
                path="/:id/additionalItems"
                element={
                  <ProtectedRoute>
                    <AdditionalItems />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:id/changeOrders"
                element={
                  <ProtectedRoute>
                    <ChangeOrderContainer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user"
                element={
                  <ProtectedRoute>
                    <UserPageContainer />
                  </ProtectedRoute>
                }
              />
              {PRODUCTION_NORTH_PRAIRIE_CONTAINER_PATHS.map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ProtectedRoute>
                      <NPContainer />
                    </ProtectedRoute>
                  }
                />
              ))}
              <Route
                path="/north-prairie"
                element={<Navigate to={PRODUCTION_NORTH_PRAIRIE_PATH} replace />}
              />
              {PRODUCTION_REPORT_CONTAINER_PATHS.map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ProtectedRoute>
                      <ProductionReportsContainer />
                    </ProtectedRoute>
                  }
                />
              ))}
              {PRODUCTION_SCHEDULE_CONTAINER_PATHS.map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ProtectedRoute>
                      <JobScheduleContainer />
                    </ProtectedRoute>
                  }
                />
              ))}
              <Route
                path="/production-schedule"
                element={<Navigate to={PRODUCTION_SCHEDULE_PATH} replace />}
              />
              {ENGINEERING_CONTAINER_PATHS.map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ProtectedRoute>
                      <EngineeringContainer />
                    </ProtectedRoute>
                  }
                />
              ))}
              <Route
                path="/engineering/design-documents"
                element={
                  <ProtectedRoute permissionKey="home.engineering-design-documents">
                    <EngineeringDesignDocuments />
                  </ProtectedRoute>
                }
              />

              {/* Redirect unknown routes to home */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
            </HelpProvider>
          </Router>
          {/* </UserRolesProvider > */}
        </div>
        </PortalRealtimeProvider>
        </SignalRProvider>
      </AuthProvider>
    </Provider>
  );
};

export default App;
